<template>
  <div v-bind:style="{ 'pointer-events': !target.isPaused && (active || target.mode == 'view' ) ? 'auto' : 'none' }">

    <div
      v-if="target.isOwner && target.blob.video.rec && !target.blob.video.src"
      class="owner-controls absolute-fill d-flex flex-column"
    >

      <!-- The space for the recording count-down -->
      <div class="flex-grow-1 d-flex justify-center align-center">
        <span v-if="target.count" class="red--text text-shadow-dark count-in">{{target.count}}</span>
      </div>

      <div class="flex-grow-0">
        <!-- The control buttons -->
        <div class="d-flex justify-center mx-n3 mb-4">
          <media-source-menu
            v-if="target.isMediaRecorderSupported"
            ref="media-source-menu"
            :mirror="target.video.mirror"
            :scale="1 / target.scale"
            @change:media-source="v => target.mediaSource = v"
            @change:mirror="v => target.video.mirror = v"
            v-slot="{ on }"
          >
            <v-btn
              v-blur v-on="on"
              class="mx-3 interactive" fab x-large dark color="secondary"
              :disabled="target.isRecording"
              @mouseup="handleSelected"
              @touchend="handleSelected"
            >
              <v-icon large>{{mdiCog}}</v-icon>
            </v-btn>
          </media-source-menu>

          <v-btn
            v-if="target.isMediaRecorderSupported"
            v-blur
            class="mx-3 interactive" fab x-large dark color="red"
            :loading="target.startCountdownStatus.pending"
            :disabled="target.startCountdownStatus.pending"
            @click="() => target.isRecording ? target.stopRecording() : target.startCountdown()"
            @mouseup="handleSelected"
            @touchend="handleSelected"
          >
            <v-icon large>{{ target.isRecording ? mdiStop : mdiRecord }}</v-icon>
          </v-btn>
        </div>

        <!-- The timeline slider -->
        <v-slider
          class="playback-slider interactive px-4" hide-details step="0"
          :disabled="target.isRecording || !isFinite(target.duration) || !target.playbackIsLoaded"
          :value="(target.playhead / target.duration) * 100.0"
          @input="target.seek"
          @start="() => target.pause()"
          @mousedown.stop
          @touchstart.native.stop
          @click.stop
        />

        <!-- The time status -->
        <div class="d-flex justify-center px-4 mb-2 font-weight-bold title">
          <template v-if="target.video.src && !target.isRecording">
            <div class="white--text text-shadow-dark">{{target.playhead | video-timestamp}}</div>
            <v-spacer />
          </template>
          <div
            class="white--text text-shadow-dark"
            v-html="target.count || (!target.video.src && !target.isRecording) ? '&nbsp;' : $options.filters['video-timestamp'](target.duration)"
          />
          <div
            v-if="target.isRecording && !target.count && target.duration > 30"
            class="red--text text-shadow-dark ml-2"
          >
            (60s limit)
          </div>
        </div>
      </div>

    </div>

    <div
      v-else
      class="default-controls absolute-fill d-flex justify-center align-center"
      v-bind:class="{ active, 'is-playing': !target.isPaused }"
      @click="() => { target.playPause(); active = true; }"
    >

      <div v-if="target.error && !static" class="d-flex flex-column align-center">
        <v-btn
          v-blur
          fab x-large dark color="warning"
          style="pointer-events: auto;"
          :loading="target.reloadStatus.pending"
          @click.stop="target.reload"
        >
          <v-icon x-large v-html="mdiRefresh" />
        </v-btn>
        <div class="mt-3 overline">Processing video…</div>
      </div>
      <v-btn
        v-else
        v-blur
        fab x-large dark color="primary"
        style="pointer-events: auto;"
        :disabled="!target.playbackIsLoaded"
        @click="target.isPaused && $emit('play-video')"
      >
        <v-icon x-large v-html="target.isPaused ? mdiPlay : mdiPause" />
      </v-btn>

      <div class="playback-details">
        <v-slider
          class="playback-slider px-4" hide-details step="0"
          :disabled="!target.playbackIsLoaded"
          :value="(target.playhead / target.duration) * 100.0"
          @input="target.seek"
          @start="target.pause"
          @click.stop
          @mousedown.stop
          @touchstart.native.stop
        />
        <div class="d-flex px-4 mb-2 font-weight-bold title white--text text-shadow-dark">
          <div>{{target.playhead | video-timestamp}}</div>
          <v-spacer />
          <div>{{target.duration | video-timestamp}}</div>
        </div>
      </div>
    </div>

  </div>
</template>


<style scoped>
.default-controls {
  cursor: pointer;
}

.default-controls.is-playing {
  transition: opacity 2s;
  opacity: 0;
}

.default-controls .playback-details {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  opacity: 0;
  pointer-events: none;

}

.default-controls.active .playback-details {
  opacity: 1;
  pointer-events: auto;
}

.default-controls.active.is-playing .playback-details {
  pointer-events: none;
}

.count-in {
  font-family: Helvetica, sans-serif;
  font-size: 180px;
  font-weight: bold;
}

.text-shadow-dark {
  text-shadow: 0 4px 16px #000000;
}

.interactive {
  pointer-events: auto;
}
</style>


<style>
.playback-slider {
  height: 24px;
}
.playback-slider .v-slider.v-slider--horizontal {
  margin-left: 0;
  margin-right: 0;
  cursor: pointer;
}
.playback-slider .v-slider--horizontal .v-slider__track-container {
  height: 8px;
}
.playback-slider .v-slider__thumb-container,
.playback-slider .v-slider__track-background,
.playback-slider .v-slider__track-fill {
  transition: none;
}
</style>


<script>
import ErrorDialog from '@/components/ErrorDialog.vue';
import geometry from '@/card-geometry-constraints.js';
import clickOutside from '@/mixins/click-outside.js';
import { mdiCog, mdiPlay, mdiPencil, mdiPause, mdiRecord, mdiStop, mdiRefresh } from '@mdi/js';


export default {
  name: 'video-blob-controls',

  components: {
    ErrorDialog,
    MediaSourceMenu: () => import('@/components/MediaSourceMenu.vue')
  },

  mixins: [ clickOutside ],

  props: [ 'target', 'static' ],

  data() {
    return {
      active: false
    };
  },

  created() {
    Object.assign(this, { mdiCog, mdiPlay, mdiPencil, mdiPause, mdiRecord, mdiStop, mdiRefresh });
  },

  mounted() {
    this.target.resolveControls(this);
  },

  methods: {
    onClickOutside(e) {
      this.active = false;
    },

    async handleSelected() {
      // We have a couple of conditional controls that only show if the blob is selected. However, blob selection is
      // triggered by the 'move' handler, which by design is tied in to 'mousedown' and 'touchstart'. So what was
      // happening is that I would mouse-down on the page in the spot where a control button *would* be showing if the
      // blob was selected, which would trigger the blob to select itself, which would then cause the control button
      // to show in this component. Now, because the 'click' event has already started, the blob control button that
      // just appeared has no way of stopping its propagation. The 'click' event then bubbles up to the card-editor's
      // 'onPageClick' method, which deselects the blob!
      // Potential solutions:
      // - Change the 'onPageClick' method to respond explicitly to 'mouseup' / 'touchend' instead of a 'click', and
      //   make sure that we stop any 'mouseup' events from propagating outside the blob-controls.
      // - Add a 'mouseup' / 'touchend' handler to any control elements that conditionally show based on selection.
      //   That handler can wait for the page click to trigger (keep in mind that 'mouseup' / 'touchend' are processed
      //   before 'click'), then reselect the blob.
      setTimeout(this.target.$parent.select);
    }
  }
}
</script>
